import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const emptyForm = {
  imei: '',
  serverIp: '193.39.187.89',
  serverPort: 5000,
  uploadIntervalSeconds: 60,
  sosNumber: '',
  momNumber: '',
  dadNumber: '',
  supervisoryStatus: 2,
  setServerConfig: true,
  restartAfter: true,
  clearWhitelist: true
};

const callModeLabel = (value) => {
  const n = Number(value);
  if (n === 1) return 'A — Listen-in (0x05=01)';
  if (n === 3) return 'C — Manual ring (0x05=03)';
  return 'B — Auto two-way (0x05=02)';
};

const DeviceCompleteSetup = () => {
  const [form, setForm] = useState(emptyForm);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  });

  const loadDefaults = useCallback(async () => {
    setLoadingDefaults(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/devices/complete-setup/defaults`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load defaults');
      }
      setForm((prev) => ({
        ...prev,
        ...data.defaults,
        imei: prev.imei
      }));
    } catch (err) {
      setError(err.message || 'Failed to load defaults');
    } finally {
      setLoadingDefaults(false);
    }
  }, []);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const appliedSettings = useMemo(() => {
    const hasContacts = Boolean(
      form.sosNumber?.trim() || form.momNumber?.trim() || form.dadNumber?.trim()
    );
    return [
      {
        setting: 'Time sync',
        protocol: '0x30',
        value: 'Server current time (IST)',
        defaultNote: 'Always on'
      },
      {
        setting: 'Data upload',
        protocol: '0x44',
        value: 'Enabled / resume upload',
        defaultNote: 'Always on'
      },
      {
        setting: 'LBS allow',
        protocol: '0x33',
        value: 'Allow LBS (0x00)',
        defaultNote: 'Default: allow'
      },
      {
        setting: 'GPS + LBS',
        protocol: '0x34',
        value: 'GPS ON + LBS ON',
        defaultNote: 'Default: both on'
      },
      {
        setting: 'Upload interval',
        protocol: '0x97',
        value: `${Number(form.uploadIntervalSeconds) || 60} seconds`,
        defaultNote: 'Default: 60s'
      },
      {
        setting: 'Do Not Disturb',
        protocol: '0x47',
        value: 'OFF (switch 0x00)',
        defaultNote: 'Not power-off'
      },
      {
        setting: 'GPS switch in 0x57',
        protocol: '0x57',
        value: 'switchByte = 0x03 (GPS on + step counter)',
        defaultNote: 'Default: GPS on'
      },
      {
        setting: 'SOS number',
        protocol: '0x41 / 0x57',
        value: form.sosNumber?.trim() || 'From DB if blank, else skip',
        defaultNote: 'Optional'
      },
      {
        setting: 'Mom number',
        protocol: '0x43 / 0x57',
        value: form.momNumber?.trim() || 'From DB if blank, else skip',
        defaultNote: 'Optional'
      },
      {
        setting: 'Dad number',
        protocol: '0x42 / 0x57',
        value: form.dadNumber?.trim() || 'From DB if blank, else skip',
        defaultNote: 'Optional'
      },
      {
        setting: 'Call protocol',
        protocol: '0x05',
        value: hasContacts || form.supervisoryStatus
          ? callModeLabel(form.supervisoryStatus)
          : 'Applied only if contacts exist',
        defaultNote: 'Default: B (two-way)'
      },
      {
        setting: 'Whitelist rebuild',
        protocol: '0x16 / 0x58',
        value: form.clearWhitelist
          ? hasContacts
            ? 'Clear + rebuild from SOS/Mom/Dad'
            : 'Skipped (no contacts entered)'
          : 'Skipped',
        defaultNote: 'Default: ON when contacts set'
      },
      {
        setting: 'Request GPS fix',
        protocol: '0x80',
        value: 'Request GPS now',
        defaultNote: 'Always on'
      },
      {
        setting: 'Server IP / Port',
        protocol: '0x66',
        value: form.setServerConfig
          ? `${form.serverIp || '—'}:${form.serverPort || '—'}`
          : 'Skipped',
        defaultNote: 'Default: 193.39.187.89:5000'
      },
      {
        setting: 'Restart after setup',
        protocol: '0x48',
        value: form.restartAfter ? 'RESTART only (0x01)' : 'Skipped',
        defaultNote: 'Never shutdown (0x02)'
      }
    ];
  }, [form]);

  const pollStatus = async (queueId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/devices/complete-setup/status/${queueId}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        setQueueStatus(data);
      }
    } catch (_) {
      /* ignore poll errors */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setResult(null);
    setQueueStatus(null);
    try {
      if (!form.imei.trim()) {
        throw new Error('Enter device IMEI / device ID');
      }
      if (form.setServerConfig && !form.serverIp.trim()) {
        throw new Error('Server IP is required when server config is enabled');
      }

      const response = await fetch(`${API_BASE_URL}/admin/devices/complete-setup`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          imei: form.imei.trim(),
          serverIp: form.serverIp.trim(),
          serverPort: Number(form.serverPort),
          uploadIntervalSeconds: Number(form.uploadIntervalSeconds),
          sosNumber: form.sosNumber.trim(),
          momNumber: form.momNumber.trim(),
          dadNumber: form.dadNumber.trim(),
          supervisoryStatus: Number(form.supervisoryStatus),
          setServerConfig: Boolean(form.setServerConfig),
          restartAfter: Boolean(form.restartAfter),
          clearWhitelist: Boolean(form.clearWhitelist)
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to queue complete setup');
      }
      setResult(data);
      if (data.queue_id) {
        pollStatus(data.queue_id);
        const timer = setInterval(() => pollStatus(data.queue_id), 5000);
        setTimeout(() => clearInterval(timer), 120000);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Complete Device Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clicking the button applies the settings listed below (defaults are pre-filled).
        Edit any field to change what will be sent. Device must come online for the queue to run.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Default settings on button click
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.9rem' }}>
          <li>Server: <strong>{form.serverIp}:{form.serverPort}</strong> (protocol 0x66)</li>
          <li>Upload interval: <strong>{form.uploadIntervalSeconds}s</strong> (0x97)</li>
          <li>GPS + LBS: <strong>ON</strong> · LBS allowed · DND OFF · GPS switch 0x03</li>
          <li>Call mode: <strong>{callModeLabel(form.supervisoryStatus)}</strong></li>
          <li>Restart after setup: <strong>{form.restartAfter ? 'Yes (restart only)' : 'No'}</strong> — never switch-off</li>
          <li>Contacts: SOS/Mom/Dad from form, or existing DB values if left blank</li>
        </Box>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {result.message}
          <Box component="div" sx={{ mt: 1, fontSize: '0.85rem' }}>
            Queue ID: {result.queue_id} · IMEI: {result.imei}
          </Box>
          {result.command_data && (
            <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result.command_data, null, 2)}
            </Box>
          )}
        </Alert>
      )}
      {queueStatus && (
        <Alert
          severity={
            queueStatus.status === 'completed'
              ? 'success'
              : queueStatus.status === 'failed'
                ? 'error'
                : 'info'
          }
          sx={{ mb: 2 }}
        >
          Queue status: <strong>{queueStatus.status}</strong>
          {queueStatus.error_message ? ` — ${queueStatus.error_message}` : ''}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Device IMEI / Device ID"
                  value={form.imei}
                  onChange={(e) => setField('imei', e.target.value)}
                  required
                  fullWidth
                  helperText="Must match inventory device_id / imei_number"
                />

                <Divider>Server connection (0x66)</Divider>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.setServerConfig}
                      onChange={(e) => setField('setServerConfig', e.target.checked)}
                    />
                  }
                  label="Write server IP / port to device"
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Server IP"
                      value={form.serverIp}
                      onChange={(e) => setField('serverIp', e.target.value)}
                      disabled={!form.setServerConfig}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="TCP Port"
                      type="number"
                      value={form.serverPort}
                      onChange={(e) => setField('serverPort', e.target.value)}
                      disabled={!form.setServerConfig}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Divider>Tracking</Divider>
                <TextField
                  label="Upload interval (seconds)"
                  type="number"
                  value={form.uploadIntervalSeconds}
                  onChange={(e) => setField('uploadIntervalSeconds', e.target.value)}
                  helperText="Default 60 seconds (protocol 0x97)"
                  fullWidth
                />

                <Divider>Contacts (optional — uses DB if blank)</Divider>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="SOS number"
                      value={form.sosNumber}
                      onChange={(e) => setField('sosNumber', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Mom number"
                      value={form.momNumber}
                      onChange={(e) => setField('momNumber', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Dad number"
                      value={form.dadNumber}
                      onChange={(e) => setField('dadNumber', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <TextField
                  select
                  label="Call protocol"
                  value={form.supervisoryStatus}
                  onChange={(e) => setField('supervisoryStatus', e.target.value)}
                  fullWidth
                  helperText="Default: B — Auto two-way"
                >
                  <MenuItem value={1}>A — Listen-in</MenuItem>
                  <MenuItem value={2}>B — Auto two-way</MenuItem>
                  <MenuItem value={3}>C — Manual ring</MenuItem>
                </TextField>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.clearWhitelist}
                      onChange={(e) => setField('clearWhitelist', e.target.checked)}
                    />
                  }
                  label="Clear & rebuild whitelist when contacts are set (default: ON)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.restartAfter}
                      onChange={(e) => setField('restartAfter', e.target.checked)}
                    />
                  }
                  label="Restart after setup (default: ON — restart only, never switch-off)"
                />

                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" disabled={submitting || loadingDefaults}>
                    {submitting ? <CircularProgress size={22} /> : 'Run Complete Setup'}
                  </Button>
                  <Button variant="outlined" onClick={loadDefaults} disabled={loadingDefaults}>
                    Reset to defaults
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Exact settings that will be applied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Live preview — updates as you change the form. This is what the button will queue.
            </Typography>
            {loadingDefaults && <CircularProgress size={22} sx={{ mb: 1 }} />}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Setting</TableCell>
                  <TableCell>Protocol</TableCell>
                  <TableCell>Value on click</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appliedSettings.map((row) => (
                  <TableRow key={row.setting}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.setting}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.defaultNote}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.protocol}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {row.value}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Switch-off is never sent. Restart uses <strong>0x48 action=restart (0x01)</strong> only.
            </Alert>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceCompleteSetup;
